/**
 * Carga masiva por Excel (bloque 13 del plan de entrega): productos con sus precios y clientes, a partir de una
 * plantilla que se descarga del propio sistema. Primero se SIMULA (qué se crearía y qué filas tienen errores) y, si
 * la dueña está de acuerdo, se aplica.
 */
import { z } from 'zod';
import { uuid } from './comun.ts';

/** Tamaño máximo del archivo ya decodificado. */
export const IMPORTAR_MAX_BYTES = 2 * 1024 * 1024;
/** Filas con datos que admite una carga. */
export const IMPORTAR_MAX_FILAS = 1000;

export const importarInput = z.object({
  /** El .xlsx en base64. */
  archivo: z
    .string()
    .min(100, 'El archivo está vacío')
    .max(Math.ceil((IMPORTAR_MAX_BYTES * 4) / 3) + 16, 'El archivo es demasiado grande (máximo 2 MB)')
    .regex(/^[A-Za-z0-9+/]+=*$/, 'El archivo no llegó completo'),
  /** true = solo revisa y devuelve lo que haría; false = aplica las filas válidas. */
  simular: z.boolean(),
  /** Los que ya existen (mismo código o RIF): true = se actualizan con lo del archivo; false = se dejan como están. */
  actualizar_existentes: z.boolean(),
});
export type ImportarInput = z.infer<typeof importarInput>;

/** Existencia inicial de un almacén: entra como un ajuste «Inventario inicial» de los productos que aún no tienen existencia allí. */
export const importarExistenciasInput = importarInput.omit({ actualizar_existentes: true }).extend({ almacen_id: uuid });
export type ImportarExistenciasInput = z.infer<typeof importarExistenciasInput>;
export const plantillaExistenciasQuery = z.object({ almacen_id: uuid });

export type AccionImportacion = 'crear' | 'actualizar' | 'omitir' | 'error';

export interface FilaImportadaDTO {
  /** Número de fila en la hoja de Excel (la 1 es la de títulos). */
  fila: number;
  /** Código del producto o RIF del cliente. */
  clave: string;
  nombre: string;
  accion: AccionImportacion;
  /** Por qué se omite o cuál es el error; en «crear» o «actualizar», avisos (p. ej. categoría nueva). */
  mensaje: string | null;
}

export interface ImportacionDTO {
  /** false = fue una simulación: nada se escribió. */
  aplicado: boolean;
  total: number;
  crear: number;
  actualizar: number;
  omitir: number;
  errores: number;
  /** Algo que conviene saber de la carga hecha (p. ej. el ajuste que generó la existencia inicial). */
  nota: string | null;
  filas: FilaImportadaDTO[];
}
